import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import CommonUrlsConfig from 'src/app/configs/common-urls.config';
import { TableConfigurationInterface } from '../sem-table/models/TableConfigurationInterface.model';
import { innerColumnName } from '../sem-table/public-api';
import { ColumnModel } from '../sem-table/table-display/columns-switch/columns/column.model';
import { LocalStorageService, storageKey } from './local-storage.service';

export enum LangEnum {
  en = 'en',
  pl = 'pl',
}

@Injectable({
  providedIn: 'root',
})
export class TranslationService {
  private readonly translateService = inject(TranslateService);
  private readonly storageHelperService = inject(LocalStorageService);

  get storageLang() {
    return this.translateService.currentLang as LangEnum;
  }

  commonUrls() {
    return CommonUrlsConfig[this.storageLang];
  }

  parseConfig(config: TableConfigurationInterface): TableConfigurationInterface {
    const { actionsButtonSlot, columns, mainToolbarSlot, selectedGloballyToolbarSlot, selectedToolbarSlot } = config;

    Object.keys(columns).forEach((key) => {
      columns[key].alias = this.mapColumnAlias(key, columns[key]);
    });

    if (actionsButtonSlot) {
      actionsButtonSlot.forEach((action) => (action.alias = this.mapActionAlias(action.actionType) || action.alias));
    }

    if (mainToolbarSlot) {
      mainToolbarSlot.forEach((action) => (action.alias = this.mapActionAlias(action.alias)));
    }

    if (selectedToolbarSlot) {
      selectedToolbarSlot.forEach((action) => (action.alias = this.mapActionAlias(action.actionType) || action.alias));
    }

    if (selectedGloballyToolbarSlot) {
      selectedGloballyToolbarSlot.forEach((action) => (action.alias = this.mapActionAlias(action.actionType) || action.alias));
    }

    return config;
  }

  setCustomLang(lang: LangEnum) {
    this.storageHelperService.save(storageKey.locale, lang);
    this.translateService.use(lang);
  }

  getCurrentLanguage(): LangEnum {
    const lang = this.translateService.currentLang || this.translateService.defaultLang;
    return Object.values(LangEnum).includes(lang as LangEnum) ? (lang as LangEnum) : LangEnum.en;
  }

  private clearAlias(alias: string): string {
    return alias.replace(/_/g, ' ');
  }

  private mapColumnAlias(key: string, column: ColumnModel) {
    const alias = column?.alias || null;

    if (column.showAliasWithoutTranslation && alias) {
      return alias;
    }

    if (key.charAt(0) === '_') {
      key = key.substring(1); // Rename mapped variable key, eg. _type -> type
    }

    if (alias !== innerColumnName && !column?.isInvisible) {
      const translateKey = `columns.${key}`;
      const translation = this.translateService.instant(translateKey);

      if (translation !== translateKey) {
        return translation;
      }
    }

    return this.clearAlias(alias || key);
  }

  private mapActionAlias(key: string) {
    const translateKey = `actions.${key}`;
    const translation = this.translateService.instant(translateKey);

    if (translation !== translateKey) {
      return translation;
    }

    return this.clearAlias(key);
  }
}
